/**
 * 子图的进出与层级状态 —— 从 Playground.vue 抽出来的第一块逻辑。
 *
 * ## 这个文件负责什么
 *
 * 编辑器支持「进到组 / 循环的内部去改」。这意味着画布上的
 * `nodes` / `edges` 只是**当前这一层**，而求值必须一直看**顶层**：
 * 进去改组的时候，外面整张图算出来的东西不能乱。
 *
 * 所以这里维护：
 *
 *   `rootNodes` / `rootEdges`   顶层那一份（求值用）
 *   `levelPath`                 进到了第几层
 *   `evalModels` / `evalEdges`  给求值用的投影（在子图里时把当前层塞回宿主）
 *   `enterSubgraph` / `exitSubgraph` / `loadLevel`
 *
 * ## ⚠️ 抽出来的方式：**函数体逐字不动**
 *
 * 状态和工具函数由宿主通过 `ctx` 注入，这里只是解构出来用 ——
 * 这样搬的时候**一行逻辑都不用改**。
 * 一次几千行的机械改名才是最容易出事的地方。
 */
import { computed, nextTick, ref } from 'vue'
/* ⚠️ 结构类型，不是引擎的类型 —— 换引擎时这一行不用改（`flowTypes.ts`） */
import type { FlowEdge as Edge, FlowNode as Node } from '@/graph/flowTypes'
import { findSocketIn } from '@/graph/linkRules'
import type { FlowStoreApi } from '@/graph/flowStore'
import {
  GROUP_INPUT_NODE,
  GROUP_OUTPUT_NODE,
  emptyGroupGraph,
  groupParamRows,
  groupRowsFor,
} from '@/node/group'
import {
  LOOP_IN_NODE,
  LOOP_OUT_NODE,
  emptyLoopGraph,
  loopParamRows,
  slotCountOf,
} from '@/node/loop'
import {
  SUBGRAPH_INTERFACE_TYPES,
  fillMissingPositions,
  refreshGroupInterface,
  refreshLoopInterface,
} from '@/node/subgraph'
import { pruneDanglingEdges, pruneNotice } from '@/graph/danglingEdges'
import type { EdgeLike } from '@/graph/danglingEdges'
import type { GraphNodeModel, SocketDef, SubGraph, SubGraphEdge } from '@/types/node'
import type { SocketType } from '@/types/socket'

/**
 * 图引擎的节点 / 边视图。
 *
 * ⚠️ 本地定义，不从 Playground 导 —— composable 不该反向依赖宿主文件，
 * 否则「抽出来」就只是换了个地方放，依赖关系还是乱的。
 */
export interface NodeView {
  id: string
  data?: { model?: GraphNodeModel; collapsed?: boolean }
  selected?: boolean
}
export interface EdgeView {
  source: string
  sourceHandle?: string | null
  target: string
  targetHandle?: string | null
  muted?: boolean
  [key: string]: unknown
}

export interface SubgraphCtx {
  /** 当前这一层的图引擎节点（进子图后是子图那一层） */
  nodes: { value: Node[] }
  edges: { value: Edge[] }
  flowStore: { value: FlowStoreApi | null }
  activeNodeId: { value: string | null }
  /** 当前这一层的模型 / 边 / 节点视图 */
  currentModels: () => GraphNodeModel[]
  edgeViews: () => EdgeView[]
  looseNodes: () => { id: string; position: { x: number; y: number } }[]
  modelOf: (id: string) => GraphNodeModel | undefined
  makeFlowNode: (model: GraphNodeModel, position: { x: number; y: number }) => Node
  makeEdge: (source: string, sourceHandle: string, target: string, targetHandle: string) => Edge
  showNotice: (message: string) => void
  unwrapArray: <T>(source: unknown) => T[]
}

export function useSubgraphLevels(ctx: SubgraphCtx) {
  const {
    nodes,
    edges,
    flowStore,
    activeNodeId,
    currentModels,
    edgeViews,
    looseNodes,
    modelOf,
    makeFlowNode,
    makeEdge,
    showNotice,
    unwrapArray,
  } = ctx

/**
 * 顶层图。
 *
 * 进入子图后 `nodes`/`edges` 换成了**子图那一层**，但求值/校验必须
 * 一直看顶层 —— 否则进去改组的时候，外面整张图算出来的东西就乱了。
 */
const rootNodes = ref<Node[]>([])
const rootEdges = ref<Edge[]>([])

/** 进入子图的路径（空 = 在顶层） */
const levelPath = ref<{ nodeId: string; title: string }[]>([])

/** 当前这一层属于哪个节点（null = 顶层） */
const currentOwnerId = computed(() =>
  levelPath.value.length ? (levelPath.value[levelPath.value.length - 1]?.nodeId ?? null) : null,
)

/**
 * 求值用的节点模型。
 *
 * ★ 在子图里时，把**当前这一层的实时状态**塞回它所属的那个节点 ——
 * 用 computed 而不是 watch 同步：随时都是最新的（没有一拍延迟），
 * 也不需要防自激（没有任何写入）。
 */
/**
 * 正在拖节点。
 *
 * ★ 拖拽期间**整条派生链都要冻住**。
 *
 * 只缓存 `evaluateGraph` 是不够的（我第一版就只做了那个，实测没解决）：
 * `evalModels` 每帧仍然返回**新数组** → `evalResult` / `problems` /
 * `diagnosticsByNode` / 输出面板**全部依次失效重算**。
 *
 * 所以关键不在这儿，而在**让 `evalModels` 返回同一个引用** ——
 * 下游看到引用没变，整条链就都不重算了。
 */
const draggingNodes = ref(false)

/** 拖拽期间冻结的模型 / 边数组（引用不变 = 下游不重算） */
let frozenModels: GraphNodeModel[] | null = null
let frozenEdges: EdgeView[] | null = null

const evalModels = computed<GraphNodeModel[]>((): GraphNodeModel[] => {
  if (draggingNodes.value && frozenModels) return frozenModels
  const next = computeEvalModels()
  frozenModels = next
  return next
})

function computeEvalModels(): GraphNodeModel[] {
  const owner = currentOwnerId.value
  if (!owner) return currentModels()

  const inner = currentModels()
  const positions = Object.fromEntries(
    looseNodes().map((n) => [n.id, { x: n.position.x, y: n.position.y }]),
  )
  const edges = edgeViews() as unknown as SubGraphEdge[]

  return (rootNodes.value as unknown as NodeView[])
    .map((n) => n.data?.model)
    .filter((m): m is GraphNodeModel => Boolean(m))
    .map((model) =>
      model.id === owner
        ? ({ ...model, graph: { nodes: inner, edges, positions } } as GraphNodeModel)
        : model,
    )
}

/** 求值用的边：在顶层就是当前的，在子图里就是顶层那份 */
const evalEdges = computed<EdgeView[]>(() => {
  if (draggingNodes.value && frozenEdges) return frozenEdges
  const next = currentOwnerId.value
    ? (rootEdges.value as unknown as EdgeView[])
    : edgeViews()
  frozenEdges = next
  return next
})

/** 把当前这一层写回它所属节点的 `graph` */
function syncLevelOut() {
  const owner = currentOwnerId.value
  if (!owner) return
  // 转成 NodeView 再找：Vue Flow 的 Node 泛型太深，直接 .find 会让 TS 推断爆栈
  const view = (rootNodes.value as unknown as NodeView[]).find((n) => n.id === owner)
  const model = view?.data?.model
  if (!model) return
  model.graph = {
    nodes: currentModels(),
    edges: edgeViews() as unknown as SubGraphEdge[],
    positions: Object.fromEntries(
      looseNodes().map((n) => [n.id, { x: n.position.x, y: n.position.y }]),
    ),
  }
  // 里面动了接口 → 外面跟着变
  refreshInterfaceOf(owner)
}

/**
 * 刷新接口之后，**顶层**可能残留指向已消失端口的边。
 *
 * ## 为什么需要它
 *
 * 循环的外部结果端口数是由**内部连线**推导的
 * （`loopRowsFromGraph` → `wiredCount` 数内部边）。
 * 用户在循环里删掉「序号 → 结果2」那条线之后，循环外部就少一个 `out1` ——
 * 而顶层那条 `n_loop.out1 → n_debug.watch.7` 还挂在原来的 handle 上。
 *
 * 后果：Vue Flow 复检既有边 → `isValidConnection` 返回 false → **丢弃该边**，
 * 并在控制台打出误导性的 `An edge needs a source and a target`。
 * 也就是「线悄没声地没了」。
 *
 * 这里主动把这种边摘掉，并告诉用户摘了哪几条 —— 与其让图引擎偷偷丢，
 * 不如我们丢得明白。
 */
function pruneDanglingRootEdges(models?: readonly GraphNodeModel[]): number {
  const before = rootEdges.value as unknown as EdgeLike[]
  if (before.length === 0) return 0
  /*
   * ⚠️ **必须用顶层模型来查，不能用 `findSocket`。**
   *
   * `findSocket` 走的是「当前这一层」（`currentModels()`）。而在退出子图的
   * 过程中，`prune` 要在 `nodes.value` 切回顶层**之前**跑（见 `loadLevel`
   * 的注释：必须赶在图引擎复检之前）。那时当前层还是**子图内部** ——
   * 拿内部的模型去查顶层的边，**每一条都解析不出来**，
   * 于是把整个图的外层边全部摘光（真出过这个事故）。
   */
  const scope = models ?? rootModels()
  const resolve = (nodeId: string, handleId: string | null | undefined) =>
    findSocketIn(scope, nodeId, handleId)
  const { kept, removed } = pruneDanglingEdges(before, resolve)
  /*
   * 只在**真的摘掉东西**时打日志 —— 每次回顶层都打会把控制台刷满。
   * 排查「线悄悄没了」时这行是唯一的现场证据，所以留着。
   */
  if (import.meta.env.DEV && removed.length > 0) {
    console.info(
      '[node-print] 顶层悬空边检查：',
      `检查 ${before.length} 条，摘掉 ${removed.length} 条`,
      removed.map((e) => e.id ?? `${e.source}.${e.sourceHandle}→${e.target}.${e.targetHandle}`),
    )
  }
  if (removed.length > 0) {
    rootEdges.value = kept as unknown as typeof rootEdges.value
    const notice = pruneNotice(removed.length)
    if (notice) showNotice(notice)
  }
  return removed.length
}

/** 顶层的节点模型（不受「当前在哪一层」影响） */
function rootModels(): GraphNodeModel[] {
  return (rootNodes.value as unknown as NodeView[])
    .map((n) => n.data?.model)
    .filter((m): m is GraphNodeModel => Boolean(m))
}

/**
 * 哪些节点能进（组 / 循环）—— 这是**应用层**的领域知识，库不该知道。
 *
 * ★ 从菜单直接拖出来的组 / 循环也要**带上内部那两个接口节点** ——
 * 否则进去是一片空白，接口没地方定义。
 */
function childGraphOf(model: GraphNodeModel): SubGraph | undefined {
  if (model.type === 'group') {
    if (!model.graph) {
      model.graph = emptyGroupGraph()
      // 外面什么都没连 → 0 个输入、0 路输出
      model.rows = groupRowsFor(0, 0)
    }
    // 位置缺了就地补 —— 否则接口节点全落在 (0,0) 叠在一起
    fillMissingPositions(model.graph, {
      [GROUP_INPUT_NODE]: { x: -1, y: 0 },
      [GROUP_OUTPUT_NODE]: { x: 1, y: 0 },
    })
    return model.graph
  }
  if (model.type === 'loop') {
    if (!model.graph) {
      /*
       * ⚠️ **不传「几路结果」** —— 结果的槽数由里面的边决定，
       * 建图时不需要外面告诉它（原来是 `emptyLoopGraph(slotCountOf(rows), 0)`，
       * 那会让「外面声明了几路」变成「里面已经有几路」）。
       */
      model.graph = emptyLoopGraph(0)
    }
    fillMissingPositions(model.graph, {
      [LOOP_IN_NODE]: { x: -1, y: 0 },
      [LOOP_OUT_NODE]: { x: 1, y: 0 },
    })
    return model.graph
  }
  return undefined
}

/**
 * 把子图里对接口的改动**反推回外部**。
 *
 * 用户在输入节点上加一行、或者给结果累记节点多接一路 —— 出来之后
 * 组 / 循环对外声明的端口就该跟着变。不这么做的话，里面加了端口
 * 外面看不见，等于白加。
 */
function refreshInterfaceOf(ownerId: string) {
  const model = (rootNodes.value as unknown as NodeView[]).find((n) => n.id === ownerId)?.data?.model
  if (!model?.graph) return

  /*
   * 内部的**结果变参行**上已经确认过类型了（进组编辑时投影会算）——
   * 镜像到外部端口时把它带上，外面才不是一片 `any`。
   */
  const resultTypes = (nodeId: string, rowId: string) => {
    const node = model.graph?.nodes.find((n) => n.id === nodeId)
    const row = node?.rows.find((r) => r.kind === 'repeater' && r.id === rowId)
    return row?.kind === 'repeater' ? row.instanceTypes : undefined
  }

  /*
   * ★ 进门前把内部接口**对齐**一下。
   *
   * ⚠️ 规矩本身**不在这里** —— 在 `node/subgraph.ts` 的
   * `refreshGroupInterface` / `refreshLoopInterface` 里，因为它是纯函数、
   * 能被直接测（这里原来内联了一份，于是「内部 vs 外部谁说了算」
   * 这件事只能靠渲染出来才知道对不对）。
   *
   * ── 规矩是什么（用户定的） ──────────────────────────────
   * **数量：里面说了算**（外面连不连都改不了组的结构 —— 想加一路参数
   * 就进组、在内部接口节点上点「＋」）。
   * **类型：外面连了什么就确认成什么**（类型不是结构，而且组内的
   * 连线校验要靠它，否则组里全是 `any`）。
   *
   * 所以这里只负责把**外面确认过的类型**算出来交给纯函数。
   */
  const confirmedInputTypes = interfaceInputTypes(model)

  if (model.type === 'group') {
    refreshGroupInterface(model, confirmedInputTypes)
  } else if (model.type === 'loop') {
    refreshLoopInterface(model, confirmedInputTypes)
  }
}

/**
 * 外面那几路接口输入**确认出来的类型**（按序号）。
 *
 * 数据源是**顶层那几条边**：外面接了一个 `text` 进 `in.0`，
 * 组内第 0 路参数就该是 `text` —— 组内的连线校验靠这个，
 * 不传的话组里全是 `any`、接什么都放行。
 *
 * ⚠️ 认不出就返回 `undefined`（那一路保持声明的类型），不要瞎猜默认值。
 */
function interfaceInputTypes(model: GraphNodeModel): (SocketType | undefined)[] | undefined {
  const rows =
    model.type === 'loop'
      ? loopParamRows(model.rows)
      : groupParamRows(model.rows)
  if (rows.length === 0) return undefined

  const edges = rootEdges.value as unknown as {
    target: string
    targetHandle?: string | null
    source: string
    sourceHandle?: string | null
  }[]
  return rows.map((row) => {
    const handle = row.socket.id
    const edge = edges.find((e) => e.target === model.id && e.targetHandle === handle)
    if (!edge) return undefined
    return socketTypeOf(edge.source, edge.sourceHandle) ?? undefined
  })
}

/**
 * ★★ 把一层的**节点和边**交给图引擎 —— 并且**顺手同步写进 store**。
 *
 * 为什么要多这一步（用户真机上报的）：
 *
 * > `[Vue Flow]: An edge needs a source and a target`
 * > `Edge: e:t2.out->p1.value`
 *
 * 那条警告不是数据错了（里面那条线好好的），而是**时序**：
 * `nodes` / `edges` 是 props，图引擎在自己的 watcher 里收进内部 store，
 * 两个 watcher 的先后不由我们决定。换层那一瞬间如果「边」先进了 store、
 * 而「节点」还是上一层那一批（外层只有这个组节点），图引擎就会认为
 * 这些边指向不存在的节点。
 *
 * ⚠️ 所以这里**先节点、后边**同步写一遍 store：顺序由我们定，
 * 边落地时节点一定已经在里面了。props 的 watcher 之后再来一遍是幂等的。
 *
 * ⚠️ 不动 `edges.value` 的赋值时机：求值读的是 `nodes` / `edges` 这两个 ref，
 * 让它们保持**同一个 tick 内一起变**，否则那一瞬间的求值会看到半张图。
 */
function swapLevel(nextNodes: Node[], nextEdges: Edge[]) {
  /*
   * 做法：**先撤边、换节点，下一拍再给边。**
   *
   * 三个阶段各自都是自洽的（不会出现「边指向不存在的节点」）：
   *   ① 旧节点 + 无边
   *   ② 新节点 + 无边
   *   ③ 新节点 + 新边
   *
   * ⚠️ 试过但**没采用**的两条路：
   *   · 直接调 store 的 `setNodes` / `setEdges`（先节点后边）—— 顺序确实由
   *     我们定了，但 store 要等图引擎 `@init` 才拿得到。也就是说**没初始化完
   *     就没用**，而且 happy-dom 里 `@init` 根本不触发 → 这条路**没法验证**。
   *     能验证的修复才叫修复。
   *   · 只调换 `nodes` / `edges` 的赋值顺序 —— 两个 watcher 谁先跑不由我们
   *     决定（真机上就是「边先进了 store」），换顺序只是碰运气。
   *
   * ⚠️ 代价（说清楚）：撤边到给边之间隔了一拍，而求值读的就是 `edges` ——
   * 换层时**那一帧**算出来的是「没有连线的图」。换层本来就在做 fitView
   * 动画，这一帧看不出来。真机上原来那条警告同时意味着图引擎**丢掉了
   * 那几条边**（它 `continue` 跳过），所以这个代价是净赚。
   */
  edges.value = [] as unknown as typeof edges.value
  nodes.value = nextNodes as unknown as typeof nodes.value
  void nextTick(() => {
    void nextTick(() => {
      edges.value = nextEdges as unknown as typeof edges.value
    })
  })
}

function enterSubgraph(nodeId: string) {
  const model = modelOf(nodeId)
  if (!model) return
  const graph = childGraphOf(model)
  if (!graph) {
    showNotice('这个节点没有内部子图')
    return
  }

  // 第一次进来：把顶层存起来（之后一直用这份做求值）
  if (levelPath.value.length === 0) {
    rootNodes.value = nodes.value
    rootEdges.value = edges.value
  } else {
    syncLevelOut()
  }

  /*
   * 循环：把内部起始节点的**参数实例数**对齐到外面已经连了几个。
   * 不对齐的话，外面连了「参数 2」进来，里面只显示一行，根本接不上。
   */
  /*
   * ★ 进门前把内部接口**对齐到外部的现状**：
   *
   *   组的输入节点 ← 外部输入变参行有几个实例，就展开成几行输出
   *   循环的起始节点 ← 外部「参数」变参行有几个实例，就展开成几行输出
   *
   * 不这么做的话，外面加的参数在里面看不见，根本接不上。
   */
  refreshInterfaceOf(nodeId)

  swapLevel(
    graph.nodes.map((m) => makeFlowNode(m, graph.positions?.[m.id] ?? { x: 0, y: 0 })) as unknown as Node[],
    graph.edges.map((e) =>
      makeEdge(e.source, e.sourceHandle ?? '', e.target, e.targetHandle ?? ''),
    ) as unknown as Edge[],
  )
  levelPath.value = [...levelPath.value, { nodeId, title: model.label || model.title }]
  clearSelection()

  nextTick(() => {
    flowStore.value?.updateNodeInternals()
    /*
     * ★ 自适应视图。
     * 子图里的坐标是从 (0,0) 附近开始的，而画布可能正停在别处 ——
     * 不 fit 的话进去看到的是空白，以为里面什么都没有。
     */
    flowStore.value?.fitView?.({ padding: 0.35, duration: 200 })
  })
  showNotice(`已进入「${model.label || model.title}」内部`)
}

/** 沿路径把某一层载进来 */
function loadLevel(path: { nodeId: string }[]) {
  let models: GraphNodeModel[] = (rootNodes.value as unknown as NodeView[])
    .map((n) => n.data?.model)
    .filter((m): m is GraphNodeModel => Boolean(m))
  let graph: SubGraph | undefined

  for (const step of path) {
    const model = models.find((m) => m.id === step.nodeId)
    graph = model?.graph
    if (!graph) break
    models = graph.nodes
  }

  if (!graph) {
    /*
     * ★ 顺序很关键：**先清悬空边，再赋值**。
     *
     * `syncLevelOut()` 已经按内部的接线重算过循环 / 组的外部端口，
     * 某个端口可能因此消失（循环的输出口数由内部接了几路决定）。
     * 如果先 `edges.value = rootEdges.value`，Vue Flow 会在**那一刻同步**
     * 拿 `isValidConnection` 复检并丢弃悬空边 —— 我们再清就晚了，
     * 控制台还会留下那句误导性的 `An edge needs a source and a target`。
     *
     * 所以：清理必须发生在把边交给图引擎**之前**。
     * （`swapLevel` 另外保证了「节点先、边后」，见它的注释。）
     */
    pruneDanglingRootEdges(rootModels())
    swapLevel(rootNodes.value as unknown as Node[], rootEdges.value as unknown as Edge[])
    return
  }

  /* ★ 同一个 `swapLevel`（节点先、边后）—— 换层的两条路都别绕开它 */
  swapLevel(
    graph.nodes.map((m) => makeFlowNode(m, graph.positions?.[m.id] ?? { x: 0, y: 0 })) as unknown as Node[],
    graph.edges.map((e) =>
      makeEdge(e.source, e.sourceHandle ?? '', e.target, e.targetHandle ?? ''),
    ) as unknown as Edge[],
  )
}

/** 退出到某一层。默认退一层；传 -1 之外的负数表示直接回顶层 */
function exitSubgraph(toIndex?: number) {
  if (levelPath.value.length === 0) return
  syncLevelOut()

  const target =
    toIndex === undefined
      ? levelPath.value.length - 1
      : toIndex < 0
        ? 0
        : Math.max(0, Math.min(levelPath.value.length, toIndex))

  levelPath.value = levelPath.value.slice(0, target)
  loadLevel(levelPath.value)
  /* 悬空边的清理放在 `loadLevel` 里、赋值给图引擎之前 —— 见那里的注释 */
  clearSelection()

  nextTick(() => {
    flowStore.value?.updateNodeInternals()
    flowStore.value?.fitView?.({ padding: 0.35, duration: 200 })
  })
  showNotice(levelPath.value.length === 0 ? '已回到顶层' : '已退出一层')
}

/* ── 端口查询 ───────────────────────────────────────────────── */

/** 按 handle 查端口定义（当前这一层的模型） */
function findSocket(
  nodeId: string,
  handleId: string | null | undefined,
): SocketDef | undefined {
  return findSocketIn(currentModels(), nodeId, handleId)
}

function socketTypeOf(
  nodeId?: string | null,
  handleId?: string | null,
): SocketType | undefined {
  if (!nodeId || !handleId) return undefined
  return findSocket(nodeId, handleId)?.type
}

/**
 * 组装一次连线判定的输入。
 *
 * 三个判定（结构合法 / 已连接 / 能否高亮）吃的是**同一个**输入结构，
 * 所以只在这里组装一次 —— 三处各拼一遍的话，加一个字段就会漏两处。
 */
function linkInput(
  source: string,
  sourceHandle: string | null | undefined,
  target: string,
  targetHandle: string | null | undefined,
) {
  return {
    models: currentModels(),
    edges: edgeViews(),
    source,
    sourceHandle,
    target,
    targetHandle,
  }
}

/**
 * 清空选中。
 *
 * `selectedIds` 是 computed（选中态在图引擎的 store 里），不能直接赋值 ——
 * 得去 store 上清。用 `unwrapArray` 而不是直接迭代：
 * 图引擎那边可能是裸数组、也可能是 ref。
 */
/**
 * 取消全部选中。
 *
 * ⚠️ 这里原来是自己遍历、`node.selected = false` **就地改**。
 * 在单窗口时代那样没错（父组件拿的就是同一批对象）。
 * 但多窗口下每个 `<VueFlow>` 挂载时都会为自己的实例生成一份节点对象，
 * 「就地改」只改到当前窗口那一份，而且**不换数组** → 宿主的文档没有变化
 * → 另一个窗口永远不会同步（实测症状：旧窗口取消不掉选中）。
 *
 * 现在改成调用图引擎自己的 `removeSelectedElements()`：它会发出
 * `nodesChange`，宿主的**唯一写入口**收到后强制换引用、重新发布文档，
 * 所有窗口一起更新。走同一条路，就不会有第二套清选中的实现。
 */
function clearSelection() {
  flowStore.value?.removeSelectedElements?.()
  activeNodeId.value = null
}

/** 组 / 循环内部的接口节点：可以选中看，但不该被删掉 */
/*
 * ⚠️ 这份名单**只有一份**（在 `node/subgraph.ts`）—— 它同时被建组那条
 * 规则（`groupableIds`）用着。以前这里叫 PROTECTED_TYPES、那边另写了一份
 * **错的**判断（拿 id 常量比 type），于是"接口节点不许收进组"从来没生效过。
 */
const PROTECTED_TYPES: readonly string[] = SUBGRAPH_INTERFACE_TYPES

function isProtectedNode(id: string): boolean {
  const model = modelOf(id)
  return Boolean(model && PROTECTED_TYPES.includes(model.type))
}

  return {
    rootNodes,
    rootEdges,
    levelPath,
    currentOwnerId,
    draggingNodes,
    evalModels,
    evalEdges,
    enterSubgraph,
    exitSubgraph,
    refreshInterfaceOf,
    findSocket,
    socketTypeOf,
    linkInput,
    clearSelection,
    isProtectedNode,
    PROTECTED_TYPES,
  }
}
